import React, { useCallback, memo, useState } from 'react';

import Area from './variants/area';
import Bar from './variants/bar';
import Cubes from './variants/cubes';
import Stack from './variants/stack';
import Dots from './variants/dots';
import Extruded from './variants/extruded';
import Line from './variants/line';
import LineSeries from './variants/line-series';
import Peak from './variants/peak';
import Pie from './variants/pie';
import Pyramid from './variants/pyramid';
import Slanted from './variants/slanted';
import SkyScraper from './variants/sky-scraper';
import UpDown from './variants/up-down';

import LabelSeries from './ui/label-series';
import Legend from './ui/legend';

import LegendUpDown from './ui/legend-updown';
import Median from './ui/median';
import Tooltip from './ui/tooltip';

import { flat, unwrap_arr } from './utils';
import { default as __ } from './utils/chart';

import { colors } from './styles';
import './styles/index.css';

const ChartBase = props => {
  const [details, set_details] = useState(false);
  const [tooltip, set_tooltip] = useState(null);

  const {
    a11yTitle,
    chart_width,
    chart_height,
    title,
    source,
    values,
    shades = [colors.accent, colors.accent_pro],
    ...other_props
  } = props;

  const chart_info = useCallback(() => {
    if (!chart_width) return null;

    const { chunkBy, topSpace } = other_props;
    const spacing = __.calc_item_width(chart_width, chunkBy);

    if (variant === 'series') {
      let min = __.calc_min(flat(values));
      let max = __.calc_max(flat(values));
      let normalized = values.map(array => {
        const v = array.map((v, i) => ({
          ...v,
          raw: v.value,
          value: __.calc_item_height(v.value, min, max, chart_height, topSpace),
          color: __.calc_color(shades, i),
        }));
        return v;
      });
      return {
        active: normalized,
        chunkBy,
        spacing,
      };
    } else {
      let min = __.calc_min(values);
      let max = __.calc_max(values);
      let total = __.calc_total(values);
      let normalized = values.map((v, i) => ({
        ...v,
        raw: v.value,
        value: __.calc_item_height(v.value, min, max, chart_height, topSpace),
        color: __.calc_color(shades, i),
      }));

      return {
        active: normalized,
        average: __.calc_average(total, normalized.length),
        chunkBy,
        min,
        max,
        origin: __.calc_origin(
          normalized,
          chunkBy,
          chart_height,
          chart_width,
          spacing,
        ),
        point: __.calc_median_point(
          normalized.length,
          total,
          min,
          max,
          chart_height,
          topSpace,
        ),
        spacing,
        total,
      };
    }
  }, [values, chart_width]);

  const {
    boundaryX,
    boundaryY,
    chartBg,
    borderWidth,
    borderColor,
    round = false,
  } = other_props;

  const a11y_label = () => {
    if (variant === 'series') {
      const with_source = source
        ? `Data source is from ${source}`
        : 'From a non specified source';
      return `${title} compares ${values.length} series of values. ${with_source}.`;
    } else {
      const { min, max, average } = chart_info();

      const with_source = source
        ? `data source is from ${source}`
        : 'from a non specified source';
      const min_value_label = unwrap_arr(values.find(v => v.value === min))
        .label;
      const max_value_label = unwrap_arr(values.find(v => v.value === max))
        .label;
      return `${title} compares ${values.length} values ${with_source}. It's max value ${max} is from ${max_value_label}. The lowest value ${min}, is from ${min_value_label}. Average of ${average}.`;
    }
  };

  const spread_props = item_props => ({
    ...chart_info(),
    onClick: d => toggle_tip(d),
    shades,
    stroke_width: borderWidth,
    stroke: borderColor,
    chart_width,
    chart_height,
    round,
    source,
    title,
    ...item_props,
  });

  const toggle_tip = (item = false) => {
    if (!item) {
      set_details(!details);
    } else {
      set_tooltip(item);
      set_details(true);
    }
  };

  const {
    labelX = false,
    labelY = false,
    legend,
    median,
    variant,
  } = other_props;

  return (
    <>
      <svg
        aria-label={a11yTitle || a11y_label()}
        style={{
          background: chartBg,
          overflow: 'visible',
        }}
        viewBox={`${boundaryX} ${boundaryY} ${chart_width} ${chart_height}`}
        width="100%"
        height={chart_height}
      >
        {
          {
            area: <Area {...spread_props()} />,
            bar: <Bar {...spread_props()} />,
            cubes: <Cubes {...spread_props()} />,
            dots: <Dots {...spread_props()} />,
            extruded: <Extruded {...spread_props()} />,
            line: <Line {...spread_props()} />,
            peak: <Peak {...spread_props()} />,
            pie: (
              <Pie
                {...spread_props({
                  values,
                })}
              />
            ),
            pyramid: <Pyramid {...spread_props()} />,
            stack: <Stack {...spread_props()} />,
            slanted: <Slanted {...spread_props()} />,
            series: <LineSeries {...spread_props()} />,
            skyscraper: <SkyScraper {...spread_props()} />,
            updown: <UpDown {...spread_props()} />,
          }[variant]
        }
        {median && <Median {...spread_props()} />}
      </svg>

      <Tooltip show={details} msg={tooltip} onClick={() => toggle_tip()} />

      {legend && <Legend {...spread_props()} />}
      {labelX && labelY && (
        <LabelSeries chart_width={chart_width} x={labelX} y={labelY} />
      )}
      {props.variant === 'updown' && <LegendUpDown {...spread_props()} />}
    </>
  );
};
const props_equal = (prevProps, nextProps) => {
  return prevProps.values === nextProps.values;
  // when the function returns true, component will not be re-rendered
};
const Chart = memo(props => <ChartBase {...props} />, props_equal);

export default Chart;
